import React, { useState, useEffect, useRef } from "react";
import { Link, graphql } from "gatsby";
import Seo from "../components/seo";
import { MapContainer, TileLayer, LayersControl, Marker, Popup, useMap, useMapEvents, Polyline } from "react-leaflet";
import L from "leaflet";
import 'leaflet/dist/leaflet.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import mapImage from "../images/assets/mapviewer.png";
import MapResizer from '../components/MapResizer';
import SetMapRef from '../components/SetMapRef';
import { Helmet } from "react-helmet";

// Import ad images directly
import a1Image from "../images/ads/a1.png";
import a2Image from "../images/ads/a2.png";

// Fix for marker image paths for Gatsby SSR/SSG
const fixLeafletIcons = () => {
  if (typeof window !== 'undefined' && L.Icon && L.Icon.Default) {
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: icon,
      iconUrl: icon,
      shadowUrl: iconShadow
    });
  }
};

// Fix Leaflet default icon issue 
let DefaultIcon = null;

if (typeof window !== 'undefined' && L.icon) {
  DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41], 
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    shadowSize: [41, 41]
  });

  // Only set the prototype if we're in the browser
  if (L.Marker && L.Marker.prototype) {
    L.Marker.prototype.options.icon = DefaultIcon;
  }
}

// Use local icons instead of remote URLs that might be blocked
// Basic marker SVGs as data URIs to avoid network requests
const markerDataURIs = {
  campground: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2325a244' width='36px' height='36px'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E",
  campsite: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ff6700' width='36px' height='36px'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E",
  restroom: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%230000ff' width='36px' height='36px'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E",
  camphost: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ffcc00' width='36px' height='36px'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E",
  food: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23ff0000' width='36px' height='36px'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E",
  mustsee: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%2300ff00' width='36px' height='36px'%3E%3Cpath d='M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z'/%3E%3C/svg%3E"
};

// Use DivIcon approach which renders reliably in React
const getMarkerIcon = (type) => {
  // Return null if we're not in the browser or L.icon isn't available
  if (typeof window === 'undefined' || !L.icon) {
    return null;
  }

  let iconUrl;
  let iconSize = [32, 32];
  let iconAnchor = [16, 32];
  
  // Normalize marker type for consistent matching
  const normalizedType = type ? type.toLowerCase() : '';
  
  if (normalizedType.includes('campsite')) {
    iconUrl = 'https://assets.sierraoutdoors.org/assets/pins/campsite.png';
  } else if (normalizedType.includes('restroom')) {
    iconUrl = 'https://assets.sierraoutdoors.org/assets/pins/restroom.png';
  } else if (normalizedType.includes('food')) {
    iconUrl = 'https://assets.sierraoutdoors.org/assets/pins/food.png';
  } else if (normalizedType.includes('mustsee')) {
    iconUrl = 'https://assets.sierraoutdoors.org/assets/pins/mustsee.png';
  } else if (normalizedType.includes('host')) {
    iconUrl = 'https://assets.sierraoutdoors.org/assets/pins/campground.png';
  } else if (normalizedType.includes('campground')) {
    iconUrl = 'https://assets.sierraoutdoors.org/assets/pins/campground.png';
  } else {
    // Default
    iconUrl = 'https://assets.sierraoutdoors.org/assets/pins/campsite.png';
    if (process.env.NODE_ENV !== 'production') {
      console.log("Using default marker icon");
    }
  }
  
  return L.icon({
    iconUrl,
    iconSize,
    iconAnchor,
    popupAnchor: [0, -32]
  });
};

// Custom zoom control component that uses the map instance directly
const ZoomControl = () => {
  const map = useMap();
  
  return (
    <div className="leaflet-control-zoom leaflet-bar leaflet-control" 
         style={{ position: 'absolute', bottom: '10px', right: '10px', zIndex: '1000' }}>
      <a 
        className="leaflet-control-zoom-in" 
        href="#" 
        title="Zoom in"
        role="button"
        aria-label="Zoom in"
        onClick={(e) => {
          e.preventDefault();
          map.zoomIn();
        }}
      >+</a>
      <a 
        className="leaflet-control-zoom-out" 
        href="#" 
        title="Zoom out"
        role="button"
        aria-label="Zoom out"
        onClick={(e) => {
          e.preventDefault();
          map.zoomOut();
        }}
      >−</a>
    </div>
  );
};

// Component to fetch and display location data
const LocationMarkers = ({ locationName, locationData }) => {
  const [markers, setMarkers] = useState([]);
  const [lines, setLines] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const map = useMap();

  // Helper to convert URLs in text to clickable links
  const linkify = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, i) =>
      urlRegex.test(part) ? (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  // Add error handling for map operations
  const safeMapOperation = (operation) => {
    try {
      if (map && !map._isDestroyed) {
        operation(map);
      }
    } catch (err) {
      console.error("Map operation failed:", err);
    }
  };

  // Modify the useEffect that uses the map
  useEffect(() => {
    if (window.updateSidebarMarkers && markers.length > 0) {
      try {
        window.updateSidebarMarkers(markers, map);
      } catch (err) {
        console.error("Failed to update sidebar markers:", err);
      }
    }
  }, [markers, map]);

  useEffect(() => {
    const processLocationData = () => {
      setIsLoading(true);
      setError(null);
      
      try {
        // Remove detailed logging
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Processing markers for ${locationName}`);
        }
        
        // Use the preloaded location data instead of fetching from API
        if (locationData && locationData.length > 0) {
          // Remove revealing log
          if (process.env.NODE_ENV !== 'production') {
            console.log(`Found ${locationData.length} markers`);
          }
          
          // First identify points that are part of lines
          const linePoints = new Set();
          locationData.forEach(loc => {
            if (loc.lineGroup && loc.lineOrder !== null) {
              // Create a unique identifier for each point used in a line
              const pointId = `${loc.latitude}-${loc.longitude}`;
              linePoints.add(pointId);
            }
          });
          
          // Filter out markers that are already part of lines
          const filteredMarkers = locationData.filter(loc => {
            // If it has line data, don't show it as a marker
            if (loc.lineGroup && loc.lineOrder !== null) {
              return false;
            }
            
            // Also check based on coordinates for points that might not have line data
            // but match coordinates of line points
            const pointId = `${loc.latitude}-${loc.longitude}`;
            return !linePoints.has(pointId);
          });
          
          setMarkers(filteredMarkers);
          
          // Process line data if needed
          const lineGroups = {};
          locationData.forEach(loc => {
            if (loc.lineGroup && loc.lineOrder !== null) {
              if (!lineGroups[loc.lineGroup]) {
                lineGroups[loc.lineGroup] = {
                  points: [],
                  color: loc.lineColor || '#3388ff',
                  weight: loc.lineWeight || 3
                };
              }
              
              // Add point to the appropriate line group
              lineGroups[loc.lineGroup].points.push({
                order: loc.lineOrder,
                latlng: [loc.latitude, loc.longitude]
              });
            }
          });
          
          // Sort points by order and prepare line data
          const processedLines = Object.keys(lineGroups).map(groupName => {
            const group = lineGroups[groupName];
            return {
              positions: group.points
                .sort((a, b) => a.order - b.order)
                .map(point => point.latlng),
              color: group.color,
              weight: group.weight
            };
          });
          
          setLines(processedLines);
          
          // Safely handle map operations with bounds using all location data
          const bounds = L.latLngBounds(
            locationData.map(loc => [
              parseFloat(loc.latitude || loc["latitude"] || loc["Latitude"]), 
              parseFloat(loc.longitude || loc["longitude"] || loc["Longitude"])
            ])
          );
          
          setTimeout(() => {
            safeMapOperation((map) => {
              map.invalidateSize();
              map.fitBounds(bounds, { padding: [50, 50] });
            });
          }, 300);
        } else {
          console.log("No markers found for this location");
          setError(`No markers found for ${locationName}`);
        }
        
        setIsLoading(false);
      } catch (error) {
        console.error("Error processing location data:", error);
        setError(`Failed to load data: ${error.message}`);
        setIsLoading(false);
      }
    };
    
    if (locationName) {
      processLocationData();
    } else {
      console.log("No location name provided for location markers");
      setIsLoading(false);
      setError("No location specified");
    }
  }, [locationName, locationData, map]);

  return (
    <>
      {isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '10px 15px',
            borderRadius: '5px',
            zIndex: 1000,
            fontSize: '14px'
          }}
        >
          Loading location data...
        </div>
      )}
      
      {error && !isLoading && (
        <div 
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(220, 53, 69, 0.9)',
            color: 'white',
            padding: '8px 15px',
            borderRadius: '5px',
            zIndex: 1000,
            fontSize: '13px',
            maxWidth: '80%',
            textAlign: 'center'
          }}
        >
          {error}
        </div>
      )}
      
      {markers.map((marker, index) => {
        // Support both camelCase and bracket notation for field access
        const lat = parseFloat(marker.latitude || marker["latitude"] || marker["Latitude"]);
        const lng = parseFloat(marker.longitude || marker["longitude"] || marker["Longitude"]);
        const markerType = marker.markerType || marker["markerType"] || marker["Marker Type"];
        const markerName = marker.markerName || marker["markerName"] || marker["Marker Name"];
        const description = marker.description || marker["description"] || marker["Description"];
        
        if (isNaN(lat) || isNaN(lng)) {
          if (process.env.NODE_ENV !== 'production') {
            console.log("Invalid coordinates for marker");
          }
          return null;
        }
        
        const icon = getMarkerIcon(markerType);
        // Remove detailed marker rendering log
        
        // Add explicit z-index to ensure markers are visible
        return (
          <Marker 
            key={`marker-${index}`}
            position={[lat, lng]}
            icon={icon}
            zIndexOffset={100 + index}
            eventHandlers={{
              click: () => {
                // Log only marker name without revealing coordinates
                if (process.env.NODE_ENV !== 'production') {
                  console.log(`Selected: ${markerName}`);
                }
              }
            }}
          >
            <Popup>
              <div className="popup-content">
                <h3><strong>{markerName || "Point"}</strong></h3>
                {description && <p>{linkify(description)}</p>}
              </div>
            </Popup>
          </Marker>
        );
      })}
      
      {lines.map((line, index) => (
        <Polyline
          key={`line-${index}`}
          positions={line.positions}
          pathOptions={{
            color: line.color || '#3388ff',
            weight: line.weight || 3
          }}
        />
      ))}
    </>
  );
};

// AdBanner component for displaying rotating ads
const AdBanner = ({ ads }) => {
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  
  // Map of image paths to imported images
  const adImages = {
    "a1.png": a1Image,
    "a2.png": a2Image
  };

  useEffect(() => {
    // Set up rotation interval
    const rotationInterval = setInterval(() => {
      setCurrentAdIndex(prevIndex => (prevIndex + 1) % ads.length);
    }, 5000);

    // Clean up on unmount
    return () => clearInterval(rotationInterval);
  }, [ads.length]);

  if (!ads || ads.length === 0) return null;

  const currentAd = ads[currentAdIndex];
  const adImage = adImages[currentAd.path];

  return (
    <div className="ad-banner-container mx-auto" style={{ maxWidth: '900px', width: '75%', maxHeight: '100%' }}>
      <a href={currentAd.url} target="_blank" rel="noopener noreferrer">
        <img 
          src={adImage} 
          alt="Sierra Outdoors" 
          className="w-full h-auto rounded-lg"
          style={{ objectFit: 'contain' }}
        />
      </a>
      <style jsx="true">{`
        @media (max-width: 768px) {
          .ad-banner-container {
            width: 100% !important;
            max-width: 100% !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          .ad-banner-container img {
            border-radius: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

const BasicGuideTemplate = ({ pageContext, data }) => {
  const [isBrowser, setIsBrowser] = useState(false);
  const mapRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMarkers, setSidebarMarkers] = useState([]);
  const [locationData, setLocationData] = useState([]);
  
  const {
    newTitle,
    introParagraph,
    generalBackground,
    howToPlanYourTrip,
    activitiesAttractions,
    insiderTips,
    pets,
    conclusion,
    latitude,
    longitude,
    section,
    slug,
    location,
    score,
    review,
    reservation,
    subTitle,
    zoom,
    center,
    bestSites,
    metaDescription,
    ogImage,
    publishDate,
    lastModifiedDate,
    author,
    keywords,
    canonicalUrl,
    locationMarkers
  } = pageContext;

  // Helper function to format content with proper paragraphs
  const formatContent = (content) => {
    if (!content) return null;
    
    // If content already contains HTML paragraph tags, return as is
    if (content.includes('<p>')) {
      return { __html: content };
    }
    
    // Split on double newlines or single newlines
    const paragraphs = content.split(/\n\n|\n/).filter(p => p.trim());
    
    // Wrap each paragraph in <p> tags
    const formattedContent = paragraphs.map(p => `<p>${p}</p>`).join('');
    
    return { __html: formattedContent };
  };

  // Convert latitude and longitude to numbers
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);

  // Check if we have valid coordinates
  const hasValidCoordinates = !isNaN(lat) && !isNaN(lng);

  // Load Geist Mono font and set isBrowser
  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsBrowser(true);
      
      // Fix Leaflet icons for Gatsby
      if (L && L.Icon) {
        fixLeafletIcons();
      }
      
      // Create a global function to update sidebar markers
      window.updateSidebarMarkers = (markers, map) => {
        // include main campground marker first, avoid duplicates
        const filtered = markers.filter(m => {
          const t = (m.markerType || m['markerType'] || m['Marker Type'] || '').toLowerCase();
          return !t.includes('campground');
        });
        setSidebarMarkers([
          { latitude, longitude, markerType: 'campground', markerName: location },
          ...filtered
        ]);
        // Store map reference for sidebar item clicks
        window.leafletMap = map;
      };
      
      // Preload marker images to ensure they're available
      const preloadImages = () => {
        const images = [
          'https://assets.sierraoutdoors.org/assets/pins/campground.png',
          'https://assets.sierraoutdoors.org/assets/pins/campsite.png',
          'https://assets.sierraoutdoors.org/assets/pins/restroom.png',
          'https://assets.sierraoutdoors.org/assets/pins/food.png',
          'https://assets.sierraoutdoors.org/assets/pins/mustsee.png'
        ];
        
        images.forEach(src => {
          const img = new Image();
          img.src = src;
        });
      };
      
      preloadImages();
      
      // Use Google Fonts CDN instead of cdnfonts which is returning 404s
      const fontLink = document.createElement('link');
      fontLink.rel = 'stylesheet';
      fontLink.href = 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;700&display=swap';
      document.head.appendChild(fontLink);
      
      // Clean up
      return () => {
        document.head.removeChild(fontLink);
        delete window.updateSidebarMarkers;
        delete window.leafletMap;
      };
    }
  }, []);

  // Function to handle sidebar marker click
  const handleMarkerClick = (lat, lng, markerName, description, index, zoom = 19) => {
    if (typeof window === 'undefined' || !window.leafletMap) {
      return;
    }
    
    // Log only in development and without coordinates
    if (process.env.NODE_ENV !== 'production') {
      console.log(`Selected from sidebar: ${markerName}`);
    }
    
    // First zoom to the marker with higher zoom level (19 instead of 17)
    window.leafletMap.setView([lat, lng], zoom);
    
    // Create and open a popup at the marker's position
    setTimeout(() => {
      if (window.leafletMap && L && L.popup) {
        const popup = L.popup()
          .setLatLng([lat, lng])
          .setContent(`
            <div class="popup-content">
              <h3><strong>${markerName || "Point"}</strong></h3>
              ${description ? `<p>${description}</p>` : ''}
            </div>
          `)
          .openOn(window.leafletMap);
      }
    }, 300); // Small delay to ensure map has finished zooming
    
    // Close sidebar on mobile after clicking
    setSidebarOpen(false);
  };

  // Now replace the map image with MapContainer
  const renderMap = () => {
    if (!hasValidCoordinates || !isBrowser) {
      return (
        <div className="map-notice">
          <p>Map coordinates not available for this location.</p>
        </div>
      );
    }

    // Remove logging of location
    if (process.env.NODE_ENV !== 'production') {
      console.log("Rendering map");
    }

    return (
      <div className="map-area" style={{ width: '100%', maxWidth: '100%', height: '600px' }}>
        {/* Hamburger menu icon */}
        <div 
          className={`hamburger-icon ${sidebarOpen ? 'open' : ''}`}
          onClick={() => setSidebarOpen(!sidebarOpen)}
        >
          <div className="bar"></div>
        </div>
        
        {/* Sidebar container */}
        <div className={`map-sidebar-container ${sidebarOpen ? 'open' : ''}`}>
          <div className="map-sidebar">
            <div className="map-sidebar-section">
              <h3>{location || 'This Location'}</h3>
              {section && (
                <div style={{ 
                  display: 'inline-block',
                  backgroundColor: '#2eb488',
                  color: '#fff',
                  padding: '0.25rem 0.5rem',
                  borderRadius: '999px',
                  fontSize: '12px',
                  marginBottom: '10px'
                }}>
                  {section}
                </div>
              )}
            </div>
            
            {/* Markers list */}
            <div className="map-sidebar-section">
              <h3>Markers</h3>
              {sidebarMarkers.length === 0 ? (
                <div className="marker-item-empty">Loading markers...</div>
              ) : (
                <div className="marker-list">
                  {/* Group header: campground name */}
                  <div
                    className="marker-item marker-group-header"
                    key="sidebar-group-main"
                    onClick={() => setGroupOpen(!groupOpen)}
                    style={{ display: 'flex', alignItems: 'center' }}
                  >
                    <img
                      src={getMarkerIcon('campground')?.options.iconUrl}
                      alt="campground"
                      style={{ width: '20px', height: 'auto', marginRight: '8px' }}
                    />
                    <div className="marker-name">
                      {groupOpen ? '▼' : '▶'} {location} ({groupMarkers.length})
                    </div>
                  </div>
                  {groupOpen && groupMarkers.map((marker, index) => {
                    const lat = parseFloat(marker.latitude || marker['latitude'] || marker['Latitude']);
                    const lng = parseFloat(marker.longitude || marker['longitude'] || marker['Longitude']);
                    const markerType = marker.markerType || marker['markerType'] || marker['Marker Type'] || 'Unknown';
                    const markerName = marker.markerName || marker['markerName'] || marker['Marker Name'] || `Marker ${index + 1}`;
                    const description = marker.description || marker['description'] || marker['Description'] || '';
                    return (
                      <div
                        className="marker-item"
                        key={`sidebar-marker-${index}`}
                        style={{ display: 'flex', alignItems: 'center' }}
                        onClick={() => handleMarkerClick(lat, lng, markerName, description, index)}
                      >
                        <img src={getMarkerIcon(markerType)?.options.iconUrl} alt={markerType} style={{ width: '20px', height: 'auto', marginRight: '8px' }} />
                        <div className="marker-name">{markerName}</div>
                      </div>
                    );
                  })}
                  {/* Other markers */}
                  {otherMarkers.map((marker, index) => {
                    const lat = parseFloat(marker.latitude || marker['latitude'] || marker['Latitude']);
                    const lng = parseFloat(marker.longitude || marker['longitude'] || marker['Longitude']);
                    const markerType = marker.markerType || marker['markerType'] || marker['Marker Type'] || 'Unknown';
                    const markerName = marker.markerName || marker['markerName'] || marker['Marker Name'] || `Marker ${index + 1}`;
                    const description = marker.description || marker['description'] || marker['Description'] || '';
                    return (
                      <div
                        className="marker-item"
                        key={`sidebar-marker-other-${index}`}
                        style={{ display: 'flex', alignItems: 'center' }}
                        onClick={() => handleMarkerClick(lat, lng, markerName, description, index)}
                      >
                        <img src={getMarkerIcon(markerType)?.options.iconUrl} alt={markerType} style={{ width: '20px', height: 'auto', marginRight: '8px' }} />
                        <div className="marker-name">{markerName}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Backdrop to close sidebar on mobile */}
        {isBrowser && sidebarOpen && (
          <div 
            className={`sidebar-backdrop ${sidebarOpen ? 'visible' : ''}`}
            onClick={() => setSidebarOpen(false)}
          />
        )}
        
        {isBrowser && (
          <MapContainer
            center={[lat, lng]}
            zoom={zoom || 12}
            scrollWheelZoom={true}
            zoomControl={false}
            style={{ width: '100%', height: '600px', maxWidth: 'none', borderRadius: '8px' }}
            className="leaflet-map-guides"
            ref={(map) => {
              if (map && mapRef.current !== map) {
                mapRef.current = map;
                // Force a resize after the map is initialized with several retries
                const resizeAttempts = [100, 300, 600, 1000];
                resizeAttempts.forEach(delay => {
                  setTimeout(() => {
                    try {
                      if (map && !map._isDestroyed) {
                        map.invalidateSize();
                      }
                    } catch (err) {
                      console.log("Map resize attempt failed, will retry");
                    }
                  }, delay);
                });
              }
            }}
            whenCreated={(map) => {
              // Additional safety measure for map creation
              setTimeout(() => {
                try {
                  if (map && !map._isDestroyed) {
                    map.invalidateSize();
                  }
                } catch (e) {
                  console.log("Map initialization safety check");
                }
              }, 200);
            }}
          >
            <MapResizer />
            <SetMapRef mapRef={mapRef} />
            <ZoomControl />
            <LayersControl position="topright" collapsed={true}>
              <LayersControl.BaseLayer name="Humanitarian">
                <TileLayer
                  url="https://tile-{s}.openstreetmap.fr/hot/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors, <a href="https://hot.openstreetmap.org/">HOT</a>'
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer name="Standard">
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
              <LayersControl.BaseLayer checked name="Satellite">
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
                  maxZoom={19}
                />
              </LayersControl.BaseLayer>
            </LayersControl>
            
            {/* Main location marker */}
            <Marker 
              position={[lat, lng]} 
              zIndexOffset={1000}
              icon={getMarkerIcon('Campground')}
            >
              <Popup>
                <div className="popup-content">
                  <h3><strong>{location || 'Campground'}</strong></h3>
                  <p>Click for more details</p>
                </div>
              </Popup>
            </Marker>
            
            {/* Display additional location markers from Google Sheets data */}
            {location && <LocationMarkers locationName={location} locationData={locationMarkers || []} />}
          </MapContainer>
        )}
      </div>
    );
  };

  const [groupOpen, setGroupOpen] = useState(false);
  const groupMarkers = sidebarMarkers.filter(m => {
    const t = (m.markerType || m['markerType'] || m['Marker Type'] || '').toLowerCase();
    return t.includes('campground') || t.includes('campsite') || t.includes('restroom');
  });
  const otherMarkers = sidebarMarkers.filter(m => {
    const t = (m.markerType || m['markerType'] || m['Marker Type'] || '').toLowerCase();
    return !(t.includes('campground') || t.includes('campsite') || t.includes('restroom'));
  });

  return (
    <>
      <Seo 
        title={newTitle}
        description={metaDescription || introParagraph?.substring(0, 160)}
        path={`/guides/${slug}`}
        image={ogImage}
        publishDate={publishDate}
        lastModifiedDate={lastModifiedDate}
        author={author}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
      />
      
      <style>
        {`
          h1, h2, h3 {
            font-family: 'Geist', sans-serif;
          }
          
          h1 {
            margin-bottom: 1.5rem;
            font-size: 2.2rem;
          }
          
          h2 {
            margin-top: 2rem;
            margin-bottom: 1.2rem;
            font-size: 1.6rem;
          }
          
          p {
            margin-bottom: 1rem;
            line-height: 1.7;
          }
          
          .main-container {
            max-width: 800px;
            margin: 0 auto;
            padding: 2rem 1rem;
          }
          
          .intro-section {
            margin-bottom: 2rem;
          }
          
          .background-section,
          .planning-section,
          .activities-section,
          .tips-section,
          .pets-section,
          .conclusion-section {
            margin-top: 3rem;
            margin-bottom: 3rem;
            padding-bottom: 1rem;
          }
          
          .pets-section em {
            display: block;
            margin-bottom: 1.2rem;
            color: #ccc;
            font-size: 0.9rem;
          }
          
          .reservation-button {
            background-color: #2eb488;
            color: white;
            padding: 10px 20px;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-weight: bold;
            text-align: center;
            text-decoration: none;
            display: inline-block;
            margin: 10px 0;
            transition: background-color 0.3s;
            font-family: 'Geist Mono', monospace;
            text-transform: uppercase;
            letter-spacing: 0.02em;
          }
          
          .reservation-button:hover {
            background-color: #259a74;
          }
          
          .review-container {
            background: linear-gradient(to top right, #002504, #002504 60%, #000000);
            border-radius: 12px;
            padding: 20px;
            margin: 20px 0;
            overflow: hidden;
          }
          
          .score-box {
            float: left;
            margin: 0 20px 10px 0;
            background-color: #2eb488;
            border-radius: 12px;
            padding: 20px;
            display: flex;
            justify-content: center;
            align-items: center;
            min-width: 100px;
            min-height: 100px;
          }
          
          .score {
            font-size: 1.8rem;
            font-weight: bold;
            color: white;
            margin: 0;
          }
          
          .review-text {
            margin: 0;
            font-size: 1rem;
            line-height: 1.6;
            color: #d6e1ec;
          }
          
          .reservation-section {
            text-align: center;
            margin-bottom: 20px;
          }
          
          @media (max-width: 768px) {
            .score-box {
              min-width: 80px;
              min-height: 80px;
              padding: 15px;
            }
            
            .score {
              font-size: 1.5rem;
            }
          }
          
          /* Back button styling */
          .back-link {
            display: inline-block;
            padding: 0.75rem 1.5rem;
            background-color: rgba(255, 255, 255, 0.1);
            color: #fff;
            text-decoration: none;
            border-radius: 8px;
            transition: background-color 0.2s ease;
            margin-bottom: 1.5rem;
          }
          
          .back-link:hover {
            background-color: rgba(255, 255, 255, 0.2);
          }
          
          /* Map area styling */
          .map-fullwidth-wrapper {
            width: 100%;
            max-width: 1200px;
            margin: 0 auto 2rem auto;
            position: relative;
          }
          
          .map-area {
            width: 100% !important;
            height: 600px !important;
            position: relative !important;
            z-index: 1 !important;
            border-radius: 8px !important;
            overflow: hidden !important;
          }
          
          @media (max-width: 991px) {
            .map-fullwidth-wrapper {
              width: 100vw !important;
              max-width: none !important;
              margin-left: -1rem !important;
              margin-right: -1rem !important;
              padding: 0 !important;
              position: relative !important;
              left: 0 !important;
            }
          }
          
          @media (max-width: 767px) {
            /* Set overflow hidden on body to prevent horizontal scroll */
            body {
              overflow-x: hidden !important;
            }
            
            /* Full viewport width approach with negative margins */
            .map-fullwidth-wrapper {
              width: 100vw !important;
              position: relative !important;
              left: 50% !important;
              transform: translateX(-50%) !important;
              margin: 0 !important;
              padding: 0 !important;
            }
            
            /* Match width for inner containers */
            .map-area, 
            .leaflet-container {
              width: 100vw !important;
              border-radius: 0 !important;
            }
          }
          
          /* Leaflet container */
          .leaflet-container {
            width: 100% !important;
            height: 600px !important;
          }
          
          /* Marker image fixes - critical for proper display */
          .leaflet-marker-icon {
            width: auto !important;
            height: auto !important;
          }
          
          /* Ensure markers are visible above other elements */
          .leaflet-marker-pane {
            z-index: 600 !important;
            pointer-events: auto !important; 
          }
          
          .leaflet-popup-pane {
            z-index: 700 !important;
          }
          
          /* Sidebar styling - Dark semi-transparent black background */
          .map-sidebar-container {
            position: absolute;
            top: 0;
            left: 0;
            width: 0;
            height: 100%;
            z-index: 999;
            overflow: hidden;
            pointer-events: none;
            transition: width 0.3s ease;
          }
          
          .map-sidebar-container.open {
            width: 300px;
            pointer-events: auto;
          }
          
          @media (max-width: 480px) {
            .map-sidebar-container.open {
              width: 260px;
            }
          }
          
          .map-sidebar {
            position: absolute;
            top: 0;
            left: 0;
            width: 300px;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.8);
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            display: flex;
            flex-direction: column;
            padding: 60px 15px 15px;
            overflow-y: auto;
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
          
          .map-sidebar-container.open .map-sidebar {
            transform: translateX(0);
          }
          
          /* Hide scrollbar for Chrome, Safari and Opera */
          .map-sidebar::-webkit-scrollbar {
            display: none;
          }
          
          /* Sidebar elements styling */
          .map-sidebar h3 {
            color: white;
            margin-bottom: 15px;
            font-size: 18px;
            border-bottom: 1px solid rgba(255, 255, 255, 0.2);
            padding-bottom: 10px;
          }
          
          .map-sidebar-section {
            margin-bottom: 20px;
          }
          
          /* Hamburger menu styling */
          .hamburger-icon {
            position: absolute;
            top: 15px;
            left: 15px;
            width: 35px;
            height: 35px;
            background-color: rgba(0, 0, 0, 0.6);
            border-radius: 4px;
            display: flex;
            justify-content: center;
            align-items: center;
            cursor: pointer;
            z-index: 1000;
          }
          
          .hamburger-icon .bar {
            width: 20px;
            height: 2px;
            background-color: white;
            position: relative;
            transition: all 0.3s ease;
          }
          
          .hamburger-icon .bar::before,
          .hamburger-icon .bar::after {
            content: '';
            position: absolute;
            width: 20px;
            height: 2px;
            background-color: white;
            transition: all 0.3s ease;
          }
          
          .hamburger-icon .bar::before {
            transform: translateY(-6px);
          }
          
          .hamburger-icon .bar::after {
            transform: translateY(6px);
          }
          
          /* Marker list styles */
          .marker-list {
            margin-top: 10px;
          }
          
          .marker-item {
            background-color: rgba(255, 255, 255, 0.1);
            border-radius: 4px;
            padding: 10px;
            margin-bottom: 8px;
            cursor: pointer;
            transition: background-color 0.3s ease;
            display: flex;
            align-items: center;
          }
          
          .marker-item:hover {
            background-color: rgba(255, 255, 255, 0.2);
          }
          
          .marker-name {
            font-weight: bold;
            color: white;
            font-size: 14px;
            margin-bottom: 4px;
          }
          
          /* Leaflet Map Control Styling - Added explicitly for guides.js */
          .leaflet-map-guides .leaflet-control-layers,
          .leaflet-map-guides .leaflet-bar a {
            background-color: rgba(0, 0, 0, 0.6) !important;
            color: white !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
          }
          
          .leaflet-map-guides .leaflet-control-layers-toggle {
            background-color: rgba(0, 0, 0, 0.6) !important;
          }
          
          .leaflet-map-guides .leaflet-control-layers-expanded {
            background-color: rgba(0, 0, 0, 0.8) !important;
            color: white !important;
          }
          
          .leaflet-map-guides .leaflet-control-layers-selector + span {
            color: white !important;
          }
          
          .leaflet-map-guides .leaflet-bar a:hover {
            background-color: rgba(0, 0, 0, 0.8) !important;
          }
          
          /* Style the layer control and zoom buttons */
          .leaflet-map-guides .leaflet-control-zoom.leaflet-bar.leaflet-control {
            background-color: transparent !important;
            border: none !important;
          }
          
          .leaflet-map-guides .leaflet-control-zoom-in,
          .leaflet-map-guides .leaflet-control-zoom-out {
            background-color: rgba(0, 0, 0, 0.6) !important;
            color: white !important;
            border: 1px solid rgba(255, 255, 255, 0.2) !important;
            width: 30px !important;
            height: 30px !important;
            line-height: 30px !important;
            font-size: 16px !important;
            text-align: center !important;
            text-decoration: none !important;
            display: block !important;
            margin-bottom: 5px !important;
            border-radius: 4px !important;
          }
          
          .leaflet-map-guides .leaflet-control-zoom-in:hover,
          .leaflet-map-guides .leaflet-control-zoom-out:hover {
            background-color: rgba(0, 0, 0, 0.8) !important;
          }
        `}
      </style>

      <div className="main-container">
        <div style={{ marginBottom: '1.5rem' }}>
          <Link 
            to="/guides/"
            className="back-link"
          >
            &larr; Back to Guides
          </Link>
        </div>
        
        <h1>{newTitle}</h1>
        
        {(score || review) && (
          <div className="review-container">
            {score && (
              <div className="score-box">
                <h2 className="score">{score}/100</h2>
              </div>
            )}
            
            {review && (
              <p className="review-text">{review}</p>
            )}
            <div style={{ clear: "both" }}></div>
          </div>
        )}
        
        {/* Ad placement after the review container */}
        <div className="mx-auto my-6">
          <AdBanner ads={data?.allAdsCsv?.nodes || []} />
        </div>
        
        {subTitle && <h2>{subTitle}</h2>}
        
        {introParagraph && (
          <div className="intro-section">
            <div dangerouslySetInnerHTML={formatContent(introParagraph)} />
          </div>
        )}
        
        <div className="reservation-section">
          <p><strong>&#x1F680; RESERVATIONS&#x1F447;</strong></p>
          {reservation && (
            <a 
              href={reservation} 
              target="_blank" 
              rel="noopener noreferrer" 
              className="reservation-button"
            >
              CURRENT STATUS &amp; RESERVATIONS
            </a>
          )}
        </div>
        
        {bestSites && (
          <div className="best-sites-section">
            <h2>&#x1F3D5; Best Campsites</h2>
            <div dangerouslySetInnerHTML={formatContent(bestSites)} />
          </div>
        )}
      </div>
      
      {hasValidCoordinates && (
        <div className="map-fullwidth-wrapper" style={{ maxWidth: '1200px', width: '100%', margin: '0 auto' }}>
          <h2 style={{ maxWidth: '800px', margin: '0 auto 1rem', padding: '0 1rem' }}>Location</h2>
          <div className="map-wrapper" style={{ width: '100%' }}>
            {renderMap()}
          </div>
        </div>
      )}
      
      {/* Second ad placement after map section */}
      <div className="mb-8 mx-auto">
        <AdBanner ads={data?.allAdsCsv?.nodes || []} />
      </div>
      
      <div className="main-container">
        {generalBackground && (
          <div className="background-section">
            <h2>&#x1F4D6; Some Quick History &amp; Background</h2>
            <div dangerouslySetInnerHTML={formatContent(generalBackground)} />
          </div>
        )}
        
        {howToPlanYourTrip && (
          <div className="planning-section">
            <h2>&#x1F9D0; Tips for First-Time Campers</h2>
            <div dangerouslySetInnerHTML={formatContent(howToPlanYourTrip)} />
          </div>
        )}
        
        {/* Ad placement before activities section */}
        <div className="mx-auto my-6">
          <AdBanner ads={data?.allAdsCsv?.nodes || []} />
        </div>
        
        {activitiesAttractions && (
          <div className="activities-section">
            <h2>&#x1F3C7; Activities to Enjoy at {location}</h2>
            <div dangerouslySetInnerHTML={formatContent(activitiesAttractions)} />
          </div>
        )}
        
        {/* Third ad placement after activities section */}
        <div className="mx-auto my-6">
          <AdBanner ads={data?.allAdsCsv?.nodes || []} />
        </div>
        
        {insiderTips && (
          <div className="tips-section">
            <h2>&#x1F4A1; Insider Tips &amp; Recommendations</h2>
            <div dangerouslySetInnerHTML={formatContent(insiderTips)} />
          </div>
        )}
        
        {pets && (
          <div className="pets-section">
            <h2>&#x1F415;&#x200D;&#x1F9D1; Pet-Friendly Guidelines at {location}</h2>
            <p><em>Regulations can change at a moment's notice. Always check with campground, state, and local laws for up to date pet regulations.</em></p>
            <div dangerouslySetInnerHTML={formatContent(pets)} />
          </div>
        )}
        
        {/* Fourth ad placement after pets section, before conclusion */}
        <div className="mx-auto my-6">
          <AdBanner ads={data?.allAdsCsv?.nodes || []} />
        </div>
        
        {conclusion && (
          <div className="conclusion-section">
            <h2>A trip you're going to remember</h2>
            <div dangerouslySetInnerHTML={formatContent(conclusion)} />
          </div>
        )}
      </div>
    </>
  );
};

export default BasicGuideTemplate;

// Add GraphQL query to fetch ads data
export const query = graphql`
  query {
    allAdsCsv {
      nodes {
        path
        url
      }
    }
  }
`;
